import {
	AbstractJastVisitor,
	COMPOUND_OPERATOR_AND,
	isOperandNode,
	type AstNode,
	type CompoundClause,
	type Jast,
} from '@atlaskit/jql-ast';

/**
 * Visitor that checks if clauses are connected with AND operators.
 * Returns true if all compound operators in the path are AND operators.
 */
export class OperatorCheckingVisitor extends AbstractJastVisitor<boolean> {
	visitCompoundClause = (compoundClause: CompoundClause): boolean => {
		// If we find an operator that's not AND, return false
		if (compoundClause.operator.value !== COMPOUND_OPERATOR_AND) {
			return false;
		}

		// Check all child clauses - if any return false, the whole path is not AND-connected
		return compoundClause.clauses.every((clause) => clause.accept(this));
	};

	// Terminal clauses don't affect the AND check
	visitTerminalClause = (): boolean => true;

	protected defaultResult(): boolean {
		return true;
	}

	protected shouldVisitNextChild(node: AstNode) {
		// Skip visiting operand subtrees, which aren't needed for clause collection and can make a big impact on performance
		return !isOperandNode(node);
	}

	protected aggregateResult(aggregate: boolean, nextResult: boolean): boolean {
		// If either result is false, the whole path is not AND-connected
		return aggregate && nextResult;
	}
}

/**
 * Returns true if the AST only contains AND operators in compound clauses.
 */
export const isOnlyAndOperator = (ast: Jast): boolean => {
	const visitor = new OperatorCheckingVisitor();
	return ast.query ? visitor.visit(ast.query) : true;
};
